import _ from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** API */
import MasterInventoryApi from 'api/master/masterInventory.api'
import InventoryApi, { PRODUCT_GROUP } from 'api/warehouses/inventory.api'

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'

/** CONSTANTS */
import { routeName } from 'routes/routes-name'
import { numberFormat } from 'utils/app.utils'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import TableRowExport from 'component/Pdf/TableRowExport'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import { dateTimeToView } from 'utils/date.utils'
import { exportInventoryToExcel } from './exportInventoryToExcel'

const permissions = {
    isCreate: isCreate(),
    isDelete: isDelete(),
    isUpdate: isUpdate(),
    isView: isView()
}

export type InventoryType = {
    amounDayBeforeExpired?: null | number
    branchId?: number
    explanation?: string
    group?: "MEDICINE" | "PRODUCTION" | "MATERIAL"
    image?: any
    inventoryId?: number
    itemCode?: string
    itemId?: number
    itemName?: string
    itemNameEn?: string
    itemTypeId?: number
    itemTypeName?: string
    itemTypeNameEn?: string
    itemUnitId?: number
    itemUnitName?: string
    itemUnitNameEn?: string
    lotNo?: string
    minStock?: number
    costPrice?: number
    salePrice?: number
    price?: number
    productFormatName?: string
    productInstructionName?: string
    productPreserveName?: string
    productUseDetail?: null | string
    qty?: number
    showOnReceipt?: "SHOW" | "HIDE"
    status?: "ACTIVE" | "INACTIVE"
    strength?: string
    total?: number
    warnings?: null | string
    files?: FilesType[]
}

export type FilesType = {
    fileId: number
    fileName: string
    filePath: string
}

export default function Inventory() {
    const { t, i18n } = useTranslation()
    const history = useHistory()
    const componentRef = useRef<any>(null)
    /** PAGINATION  */
    const [page, setPage] = useState(1)
    const [pageLimit, setPageLimit] = useState(10)
    const [rowCount, setRowCount] = useState(0)
    const [sortBy, setSortBy] = useState('createAt')
    const [sortType, setSortType] = useState('ASC')

    const selectPCRef = useRef<any>(null)
    const [selectPCWidth, setSelectPCWidth] = useState<number>(112)
    const selectOPRef = useRef<any>(null)
    const [selectOPWidth, setSelectOPWidth] = useState<number>(112)
    const [search, setSearch] = useState('')
    const [group, setGroup] = useState<string>('ALL')
    const [category, setCategory] = useState<any>('ALL')
    const [stock, setStock] = useState<any>('ALL')

    const [productsCategory, setProductsCategory] = useState<any[]>([])
    const [productCategoryLoading, setProductCategoryLoading] = useState(false)
    const [inventory, setInventory] = useState<any[]>([])
    const [exportData, setExportData] = useState<any[]>([])
    const optionStock = [
        { value: 'ALL', label: t('ALL') },
        { value: 'NORMAL', label: t('INVENTORY.FILTER.NORMAL') },
        { value: 'MIN', label: t('INVENTORY.FILTER.LOWER_STOCK') }
    ]


    const handleChangeCategory = (value: string) => {
        setCategory(value)
    }

    const handleChangeStock = (value: string) => {
        setStock(value)
    }

    const handleChangeGroup = (status: string) => {
        setGroup(status)
    }

    const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }

    const handleChangeRowsPerPage = (limit: number) => {
        setPageLimit(limit)
    }

    const handleChangePage = (val: number) => {
        setPage(val)
    }

    const onView = (id: number) => {
        history.push(routeName.inventory + '/view', { inventoryId: id })
    }

    const loadProductType = (searchText: any) => {
        setProductCategoryLoading(true)
        MasterInventoryApi.findAllProductsType({ pageLimit: 50, search: searchText })
            .then(({ data }) =>
                setProductsCategory([
                    { productTypeId: 'ALL', productTypeName: t('INVENTORY.FILTER.CATEGORY.ALL') },
                    ...data
                ])
            )
            .finally(() => setProductCategoryLoading(false))
    }


    const loadData = () => {
        let condition: any = {}
        if (page) condition = { ...condition, page: page }
        if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
        if (search) condition = { ...condition, search: search }
        if (sortBy) condition = { ...condition, sortBy: sortBy }
        if (sortType) condition = { ...condition, sortType: sortType }
        if (group) condition = { ...condition, group: group === 'ALL' ? '' : group }
        if (category) condition = { ...condition, itemTypeId: category === 'ALL' ? '' : category }
        if (stock) condition = { ...condition, stockType: stock === 'ALL' ? '' : stock }
        InventoryApi.findAll(condition).then(({ data, headers }) => {
            setInventory(data)
            setRowCount(headers['x-total'])
        })
            .catch(() => { return })
    }

    const loadDataForExport = () => {
        let condition: any = {}
        condition.export = 1
        if (page) condition = { ...condition, page: page }
        if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
        if (search) condition = { ...condition, search: search }
        if (sortBy) condition = { ...condition, sortBy: sortBy }
        if (sortType) condition = { ...condition, sortType: sortType }
        if (group) condition = { ...condition, group: group === 'ALL' ? '' : group }
        if (category) condition = { ...condition, itemTypeId: category === 'ALL' ? '' : category }
        if (stock) condition = { ...condition, stockType: stock === 'ALL' ? '' : stock }
        InventoryApi.findAll(condition)
            .then(({ data }) => setExportData(data))
    }

    useEffect(() => {
        loadProductType('')
        loadData()
        loadDataForExport()
    }, [page, pageLimit, sortBy, sortType])

    const handleSearch = () => {
        setPage(1)
        loadData()
        loadDataForExport()
    }

    const headCells = [
        { id: 'no', label: t('TABLE.NO'), width: '50px', align: 'center' },
        { id: 'group', label: t('INVENTORY.TABLE.CELLS.TYPE'), width: '130px' },
        { id: 'product', label: t('INVENTORY.TABLE.CELLS.PRODUCT') },
        { id: 'price', label: t('INVENTORY.TABLE.CELLS.PRICE'), width: '200px', align: 'right' },
        { id: 'qty', label: t('INVENTORY.TABLE.CELLS.INVENTORY_QTY'), width: '150px', align: 'center' },
        { id: 'unit', label: t('INVENTORY.TABLE.CELLS.UNIT'), width: '160px', align: 'center' },
        { id: 'updatedBy', label: t('INVENTORY.TABLE.CELLS.UPDATED_BY'), width: '200px' },
        { id: 'action', label: 'Action', width: '100px' }
    ]


    const renderData = (objData: any, no: number, valueExportData: boolean) => {
        const { inventoryId, group, itemName, itemNameEn, price, qty, itemUnitName, itemUnitNameEn, updatedBy, updatedAt, minStock } = objData

        no = page * pageLimit - pageLimit + no + 1
        const objRenderData = {
            key: no.toString(),
            id: no.toString(),
            obj: objData,
            columns: [
                { option: 'TEXT', align: 'center', label: no },
                { option: 'TEXT', align: 'left', label: group ? t(`INVENTORY.FILTER.GROUP.${group}`) : '-' },
                { option: 'TEXT', align: 'left', label: itemName || itemNameEn || '-', class: 'text-ellipsis' },
                { option: 'TEXT', align: 'right', label: numberFormat(price || 0) },
                { option: 'TEXT', align: 'center', label: qty >= minStock ? <Typography >{qty || '-'}</Typography> : <Typography color="red" >{qty || '-'}</Typography> },
                { option: 'TEXT', align: 'center', label: itemUnitName || itemUnitNameEn || '-' },
                { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
                {
                    option: 'ACTION',
                    align: 'center',
                    label: 'action',
                    values: [
                        { option: 'VIEW', label: t(`BUTTON.VIEW`), disabled: permissions.isView.disabled },
                    ]
                }
            ]
        }

        if (valueExportData) objRenderData.columns.pop()

        return (
            valueExportData === true &&
            <TableRowExport {...objRenderData} /> ||
            <TableRowCommon  {...objRenderData} onView={() => onView(inventoryId)} />
        )
    }

    const handleWidthPC = () => {
        if (selectPCRef) {
            setSelectPCWidth(selectPCRef?.current?.clientWidth + 5)
        } else {
            setSelectPCWidth(112)
        }
    }

    const handleWidthOP = () => {
        if (selectPCRef) {
            setSelectOPWidth(selectOPRef?.current?.clientWidth + 5)
        } else {
            setSelectOPWidth(112)
        }
    }

    useEffect(() => {
        handleWidthPC()
        handleWidthOP()
    }, [selectPCRef, selectOPRef, i18n.language])

    return (
        <Box>
            <HeaderCard text={t('INVENTORY.HEADER.DEFAULT')} />
            <Box paddingX="24px" paddingTop="16px">
                <Grid container spacing="16px">
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <TextInput
                            label={t('INVENTORY.SEARCH')}
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value)
                            }}
                            fullWidth
                            inputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" >
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('');
                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput
                            label={t('INVENTORY.FILTER.GROUP.LABEL')}
                            onChange={e => {
                                const value = e.target.value;
                                handleChangeGroup(value);

                            }}
                            options={[
                                { value: 'ALL', name: t('INVENTORY.FILTER.GROUP.ALL') },
                                { value: PRODUCT_GROUP.PRODUCT, name: t(`INVENTORY.FILTER.GROUP.${PRODUCT_GROUP.PRODUCT}`) },
                                { value: PRODUCT_GROUP.MEDICINE, name: t(`INVENTORY.FILTER.GROUP.${PRODUCT_GROUP.MEDICINE}`) },
                                { value: PRODUCT_GROUP.MATERIAL, name: t(`INVENTORY.FILTER.GROUP.${PRODUCT_GROUP.MATERIAL}`) },
                            ]}
                            value={group}
                            width="100%"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput
                            label={t('INVENTORY.FILTER.CATEGORY.LABEL')}
                            onChange={e => {
                                handleChangeCategory(e.target.value);
                            }}
                            options={[
                                ...productsCategory.map((item: any) => ({
                                    value: item.productTypeId,
                                    name: item.productTypeName
                                }))
                            ]}
                            value={category}
                            loading={productCategoryLoading}
                            width="100%"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput
                            label={t('INVENTORY.FILTER.STOCK.LABEL')}
                            onChange={e => {

                                handleChangeStock(e.target.value);
                            }}
                            options={[
                                ...optionStock.map((item: any) => ({
                                    value: item.value,
                                    name: item.label
                                }))
                            ]}
                            value={!_.isEmpty(stock) ? stock : ''}
                            width="100%"
                        />

                    </Grid>
                    <Grid item xs={12} sm={12} md={12} xl={4}>
                        <Box display={'flex'} justifyContent={'space-between'}>
                            <MainButton
                                title={t('INPUT.SEARCH')}
                                type='primary'
                                variant='contained'
                                style={{ width: '120px' }}
                                onClick={handleSearch} />
                            <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                                if (e === 'Excel') {
                                    const report: any = {
                                        title: t('INVENTORY.HEADER.DEFAULT'),
                                        statusFilter: `${t('INVENTORY.FILTER.GROUP.LABEL')}: ${group === 'ALL' ? t('INVENTORY.FILTER.GROUP.ALL') : t(`INVENTORY.FILTER.GROUP.${group}`)}   ${t('INVENTORY.FILTER.CATEGORY.LABEL')}: ${_.find(productsCategory, (item) => item.productTypeId === category)?.productTypeName || t('INVENTORY.FILTER.GROUP.ALL')}  ${t('INVENTORY.FILTER.STOCK.LABEL')}: ${_.find(optionStock, (item) => item.value === stock)?.label || t('INVENTORY.FILTER.GROUP.ALL')}`,
                                        rows: exportData.map((item: any) => ({
                                            group: item.group ? t(`INVENTORY.FILTER.GROUP.${item.group}`) : '-',
                                            itemName: item?.itemName || '-',
                                            price: item?.price || '-',
                                            qty: item?.qty || '-',
                                            minStock: item?.minStock || '-',
                                            itemUnitName: item?.itemUnitName || item?.itemUnitNameEn || '-',
                                            updatedBy: item?.updatedBy,
                                            updatedAt: dateTimeToView(item?.updatedAt)
                                        })),
                                        t: t
                                    }
                                    exportInventoryToExcel(report)
                                } else if (e === 'PDF') {
                                    const style = document.createElement("style");
                                    style.innerHTML = `
                                    @media print {
                                        @page { size: portrait }
                                    }
                                `;
                                    document.head.appendChild(style);
                                    window.print();
                                    document.head.removeChild(style);

                                }
                            }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                        </Box>
                    </Grid>
                </Grid>
                <Box paddingTop="24px">
                    <TableCustom
                        page={page}
                        pageLimit={pageLimit}
                        sortType={sortType}
                        sortBy={sortBy}
                        onSort={onRequestSort}
                        setPageLimit={handleChangeRowsPerPage}
                        setPage={handleChangePage}
                        rowCount={rowCount}
                        headCells={headCells}
                        rowsData={inventory.map((val, i) => {
                            return renderData(val, i, false)
                        })}
                    />
                </Box>
            </Box>

            <Box
                id="print-table-container"
                ref={componentRef}
            >
                <Typography fontSize="14px" fontWeight={400}>{t('INVENTORY.HEADER.DEFAULT')}</Typography>
                <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
                    <Typography fontSize="10px" fontWeight={400}>{t('INVENTORY.FILTER.GROUP.LABEL')}: {group === 'ALL' ? t('INVENTORY.FILTER.GROUP.ALL') : t(`INVENTORY.FILTER.GROUP.${group}`)}</Typography>
                    <Typography fontSize="10px" fontWeight={400}>{t('INVENTORY.FILTER.CATEGORY.LABEL')}: {_.find(productsCategory, (item) => item.productTypeId === category)?.productTypeName || 'ทั้งหมด'}</Typography>
                    <Typography fontSize="10px" fontWeight={400}>{t('INVENTORY.FILTER.STOCK.LABEL')}: {_.find(optionStock, (item) => item.value === stock)?.label || 'ทั้งหมด'}</Typography>
                </Box>
                <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
                    <thead>
                        <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
                            <th style={{ borderBottom: '1px solid black' }}>{t('#')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('INVENTORY.TABLE.CELLS.TYPE')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('INVENTORY.TABLE.CELLS.PRODUCT')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('INVENTORY.TABLE.CELLS.PRICE')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('INVENTORY.TABLE.CELLS.INVENTORY_QTY')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('INVENTORY.TABLE.CELLS.UNIT')}</th>
                            <th style={{ borderBottom: '1px solid black' }}>{t('INVENTORY.TABLE.CELLS.UPDATED_BY')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {exportData?.length === 0 &&
                            <tr>
                                <td colSpan={7} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100%' }}>{t('NOT_FOUND')}</td>
                            </tr>
                        }
                        {exportData?.map((data: any, index: number) => (
                            <tr key={index}>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{index + 1}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '85px' }}>{data?.group ? t(`INVENTORY.FILTER.GROUP.${data?.group}`) : '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{data?.itemName || data?.itemNameEn || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center', width: "100px" }}>{numberFormat(data?.price || 0)}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: "85px", color: data?.qty >= data?.minStock ? 'black' : 'red' }}>{data?.qty}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: "75px" }}>{data?.itemUnitName || data?.itemUnitNameEn || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                    <span>{data?.updatedBy || '-'}</span>
                                    <span>{dateTimeToView(data?.updatedAt) || '-'}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Box>
        </Box>
    )
}